import * as React from 'react';

/**
 * JavaScript triggered keyboard event from an input field will not work with React onKeyDown synthetic event.
 * Pass the event handler that would've been passed to onKeyDown prop of an input field.
 * Pass the event handler that would've been passed to onInput prop of an input field.
 * Pass the event handler that would've been passed to onChange prop of an input field.
 * The return value is a ref object that can be used as the `ref` prop of the input field.
 */
export const useScanInput = ({
  onKeyDown,
  onInput,
  onChange,
}: {
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onInput?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}): React.RefObject<HTMLInputElement> => {
  const inputRef = React.createRef<HTMLInputElement>();
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (onKeyDown) onKeyDown(event as unknown as React.KeyboardEvent<HTMLInputElement>);
    };
    const handleInput = (event: Event) => {
      if (onInput) onInput(event as unknown as React.ChangeEvent<HTMLInputElement>);
    };
    const handleChange = (event: Event) => {
      if (onChange) onChange(event as unknown as React.ChangeEvent<HTMLInputElement>);
    };

    const inputElement = inputRef.current;

    if (inputElement) {
      // Binding directly on onkeypress to allow ScanHandler to detect that this input field is expecting key events.
      inputElement.onkeypress = (event: KeyboardEvent) => {
        handleKeyDown(event);
      };
      // Binding directly on oninput to simplify unbinding of event listener.
      inputElement.oninput = (event: Event) => {
        handleInput(event);
      };
      // Binding directly on onchange to simplify unbinding of event listener.
      inputElement.onchange = (event: Event) => {
        handleChange(event);
      };
      // Allow ScanHandler to detect field presence on the page
      inputElement.classList.add('inputbarcode');
    }

    return () => {
      if (inputElement) {
        inputElement.onkeypress = null;
        inputElement.oninput = null;
        inputElement.onchange = null;
        inputElement.classList.remove('inputbarcode');
      }
    };
  }, [inputRef, onChange, onInput, onKeyDown]);
  return inputRef;
};
